import styled from 'styled-components'
import './leftbar-local-overrides.css'
import logo from '../../../../assets/licito-logo.svg'
import History from './history/history'
import {useContext, useState, useRef, useEffect, useMemo} from 'react'
import HideShowBarButton from '../../../../globalComponents/hideShowBarButton'
import {AuthContext} from '../../../../contexts/auth'
import NavList from './navList'
import {HistoryContext} from '../../../../contexts/history'
import {
    Button as MuiButton,
    IconButton,
    InputAdornment,
    TextField,
    Tooltip,
} from '@mui/material'
import {useLocation, useNavigate} from 'react-router-dom'
import {
    MessagesSquare,
    Folder,
    FileSearch,
    Receipt,
    Compass,
    Radar,
    Handshake,
    GraduationCap,
    LifeBuoy,
    Search,
    PanelLeft,
    Settings,
} from 'lucide-react'
import {getCurrentColors} from '../../../../utils/colors'
import {getCurrentImages} from '../../../../utils/logos'

import {useJwt} from 'react-jwt'
import {CookiesService} from '../../../../services/Cookie.service'

const COLORS = getCurrentColors()
const IMAGES = getCurrentImages()
const LEFTBAR_WIDTH = '300px'

// Paleta de destaque exclusiva do menu lateral (alinhada ao protótipo de
// referência). Mantida local para não afetar COLORS.primaria/secundaria,
// que são usadas em outras ~20 telas do app.
const NAV_ACCENT_BLUE_HOVER = '#1769c4'
const NAV_ACCENT_BLUE_WASH = '#eaf3fd'
const NAV_ACCENT_TEAL_WASH = '#e7f8f3'
const NAV_ACCENT_TEAL_INK = '#0e8f78'

const NewBadge = styled.span`
    margin-left: auto;
    background-color: ${NAV_ACCENT_TEAL_WASH};
    color: ${NAV_ACCENT_TEAL_INK};
    font-size: 9px;
    font-weight: 700;
    padding: 3px 7px;
    border-radius: 20px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    white-space: nowrap;
    flex-shrink: 0;
`

const Section = styled.section<{show: boolean}>`
    width: ${({show}) => (show ? LEFTBAR_WIDTH : '0px')};
    min-width: ${({show}) => (show ? LEFTBAR_WIDTH : '0px')};
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    transform: translateX(${({show}) => (show ? '0' : '-100%')});
    transition: transform 0.3s ease-in-out;
    z-index: 55;
    border-right: 1px solid #e6eaec;
    @media (max-width: 640px) {
        position: absolute;
        min-height: 100vh;
    }
    overflow: hidden;
    background-color: #ffffff;
`

const ScrollWrapper = styled.div`
    flex: 1;
    min-height: 0;
    width: 100%;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
`

const TopFixed = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 20px;
    padding-top: 16px;
`

const NavGroup = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 2px;
`

const ScrollArea = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
`

const BottomFixed = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    flex-shrink: 0;
    position: relative;
    padding-bottom: 20px;
`

const Divider = styled.div`
    height: 1px;
    background-color: #e6eaec;
    width: calc(100% - 28px);
    margin: 10px 14px 12px;
    flex-shrink: 0;
`

const SupportButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 9px;
    width: calc(100% - 28px);
    margin: 0 14px 12px;
    padding: 10px;
    border: none;
    border-radius: 10px;
    background-color: ${COLORS.secundaria};
    color: ${COLORS.secundariaEscura};
    font-size: 13px;
    font-weight: 600;
    font-family: inherit;
    cursor: pointer;
    transition: filter 0.15s;

    &:hover {
        filter: brightness(0.97);
    }
`


const Logo = styled.img`
    width: auto;
    height: 40px;
    margin-left: 10px;
`
const SeconderyLogo = styled.img`
    width: 115px;
    height: 10px;
    margin-left: 15px;
`

const ButtonsContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 4px;
    align-items: flex-start;
`

const ProfileRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    width: calc(100% - 28px);
    margin: 0 14px;
    padding: 8px 0;
`

const ProfileInfo = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
`

const ProfileName = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: #0c1f29;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`

const ProfileEmail = styled.span`
    font-size: 11px;
    color: #8a969c;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`
const CreditsContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: calc(100% - 32px);
    gap: 6px;
    margin: 4px 16px 10px;
    padding: 2px 4px;
`
const CreditsContainerTop = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    font-size: 12px;
    color: #5d6b72;
`

const CreditsBar = styled.div`
    width: 100%;
    height: 5px;
    background-color: #f2f5f6;
    border-radius: 20px;
    overflow: hidden;
`

const CreditsBarFill = styled.div<{percent: number}>`
    height: 100%;
    width: ${({percent}) => `${percent}%`};
    background: linear-gradient(
        120deg,
        ${COLORS.primariaEscura},
        ${COLORS.secundariaEscura}
    );
    border-radius: 20px;
    transition: width 0.3s ease;
`
const CreditsContainerBottom = styled.div<{pathname: string}>`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    width: 220px;
    height: 36px;
    border: 1px solid
        ${({pathname}) => {
            return pathname === '/' ? COLORS.secundaria : COLORS.primaria
        }};
    border-radius: 10px;
    margin-left: 16px;
    color: ${({pathname}) => {
        return pathname === '/' ? COLORS.secundaria : COLORS.primaria
    }};
    cursor: pointer;
`

interface Props {
    visibility: string
}

const Alert = styled.div<Props>`
    color: ${(props) => props.visibility};
    transition: color 0.25s;
`

interface NavButtonProps {
    tooltip: string
    icon: React.ReactNode
    label: string
    active: boolean
    activeColor?: string
    activeTextColor?: string
    onClick: () => void
    isNew?: boolean
}

function NavButton({
    tooltip,
    icon,
    label,
    active,
    activeColor = COLORS.primaria,
    activeTextColor = COLORS.primariaEscura,
    onClick,
    isNew,
}: NavButtonProps) {
    return (
        <div style={{width: 'calc(100% - 28px)', marginLeft: '14px'}}>
            <Tooltip title={tooltip} placement="right">
                <MuiButton
                    onClick={onClick}
                    startIcon={icon}
                    aria-current={active ? 'page' : undefined}
                    sx={{
                        border: 'none',
                        borderRadius: '9px',
                        justifyContent: 'flex-start',
                        width: '100%',
                        fontSize: '13px',
                        fontWeight: active ? 500 : 400,
                        textTransform: 'none',
                        color: active ? activeTextColor : COLORS.cinzaEscuro,
                        backgroundColor: active ? activeColor : 'transparent',
                        padding: '10px 11px',
                        gap: '11px',
                        transition: 'background-color 0.14s, color 0.14s',
                        '& .MuiButton-startIcon': {
                            marginRight: 0,
                            marginLeft: 0,
                        },
                        '&:hover': {
                            backgroundColor: active
                                ? activeColor
                                : COLORS.cinzaClaro,
                            border: 'none',
                        },
                    }}
                >
                    <span
                        style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flex: isNew ? '0 1 auto' : 1,
                            textAlign: 'left',
                        }}
                    >
                        {label}
                    </span>
                    {isNew && <NewBadge>Novo</NewBadge>}
                </MuiButton>
            </Tooltip>
        </div>
    )
}

export default function LeftBar() {
    const [show, setShow] = useState(() => {
        if (typeof window !== 'undefined') {
            return window.innerWidth > 1090
        }
        return true
    })
    const {userInfo} = useContext(AuthContext)
    const {createNewChat, limits, alertVisibility, isChat, setIsChat, selected} =
        useContext(HistoryContext)
    const [openList, setOpenList] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const listRef = useRef<HTMLDivElement | null>(null)
    const navigate = useNavigate()
    const handleChatsClick = () => {
        setIsChat(true)
        createNewChat()
        navigate('/')
    }
    const handleDocumentosClick = () => {
        setIsChat(false)
        navigate('/projetos')
    }
    const createInitialsSVG = (firstName: string, lastName?: string) => {
        const initials =
            (firstName?.[0] || '').toUpperCase() +
            (lastName?.[0] || '').toUpperCase()
        const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" width="30" height="30">
            <rect width="40" height="40" rx="11" fill="#012441"/>
            <text x="20" y="25" text-anchor="middle" fill="white" font-size="16" font-family="Arial">${initials}</text>
            </svg>`
        return `data:image/svg+xml;base64,${btoa(svg)}`
    }
    const location = useLocation()
    const [logoSrc, setLogoSrc] = useState(logo)
    const cookieService = useMemo(() => new CookiesService(), [])
    const [authToken, setAuthToken] = useState<string>('')

    useEffect(() => {
        const authData = cookieService.getAuthData()
        if (authData?.access_token) {
            setAuthToken(authData.access_token)
        }
    }, [cookieService])

    const {decodedToken} = useJwt(authToken)

    type DecodedAuthToken = {
        sub: string
        name: string
        id: string
        scopes: string[]
        organization: {
            id: string
            name: string
        }
        modules: string[]
        assistants: string[]
        exp: number
        iat: number
    }

    const modules = useMemo(() => {
        if (!decodedToken) return []
        const typedToken = decodedToken as DecodedAuthToken
        return typedToken.modules ?? []
    }, [decodedToken])

    const featureAccess = useMemo(() => {
        const hasModule = (moduleKeys: string | string[]) => {
            if (!modules || modules.length === 0) return false
            const keys = Array.isArray(moduleKeys) ? moduleKeys : [moduleKeys]

            const normalizedKeys = keys.map((key) => key.toLowerCase())

            return modules.some((module) =>
                normalizedKeys.includes(module.toLowerCase())
            )
        }

        return {
            chats: hasModule(['chat']),
            documents: hasModule(['document_generator']),
            analyzer: hasModule(['document_analyzer']),
            priceResearch: hasModule(['price_research']),
            carona: hasModule(['carona_inteligente']),
            // Radar de Fornecedor vive no serviço de pesquisa de preços e usa o mesmo acesso
            // (o backend não exige claim próprio). Trocar p/ 'radar_fornecedor' se o core passar
            // a emitir um módulo dedicado.
            radar: hasModule(['price_research']),
            // Levantamento de Mercado (F1) reusa o claim comercial da Pesquisa de
            // Preços. Trocar p/ 'levantamento_mercado' se o core emitir claim próprio.
            // Gate adicional pela config do ambiente: sem VITE_LEVANTAMENTO_URL no
            // build (ex.: prod antes do deploy do levantamento-service), o item NÃO
            // aparece — evita expor módulo morto a todo org com price_research.
            levantamento:
                hasModule(['price_research']) &&
                !!import.meta.env.VITE_LEVANTAMENTO_URL,
            // Trilha do Guru: módulo `academy_service` do plan setup. Gate
            // adicional pela flag de ambiente: só VITE_TRILHA_GURU_ENABLED=true
            // no build exibe o item. Comparação estrita de propósito — env de
            // Vite é sempre string, e por truthiness até 'false' habilitaria.
            trilha:
                hasModule(['academy_service']) &&
                import.meta.env.VITE_TRILHA_GURU_ENABLED === 'true',
        }
    }, [modules])

    const {
        chats: canAccessChats,
        documents: canAccessDocuments,
        analyzer: canAccessAnalyzer,
        priceResearch: canAccessPriceResearch,
        carona: canAccessCarona,
        radar: canAccessRadar,
        levantamento: canAccessLevantamento,
        trilha: canAccessTrilha,
    } = featureAccess

    const isProcon = window.location.hostname.includes('procon')

    useEffect(() => {
        if (!authToken || modules.length === 0) return
        console.log('Decoded Token Modules:', modules)
    }, [authToken, modules])

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (
                listRef.current &&
                !listRef.current.contains(event.target as Node)
            ) {
                setOpenList(false)
            }
        }
        if (openList) {
            document.addEventListener('mousedown', handleClickOutside)
        } else {
            document.removeEventListener('mousedown', handleClickOutside)
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [openList])

    const messageCreditsCurrent = limits?.usages?.messages?.current ?? 0
    const messageCreditsMax = limits?.usages?.messages?.max ?? 0
    const projectCreditsCurrent = limits?.usages?.projects?.current ?? 0
    const projectCreditsMax = limits?.usages?.projects?.max ?? 0
    const isProjectsPage = location.pathname === '/projetos'
    // Trilha do Guru é módulo livre: não consome créditos, então não exibimos
    // saldo nem barra de consumo enquanto o usuário estiver nela.
    const isTrilhaPage = location.pathname.startsWith('/trilha-guru')
    const creditsCurrent = isProjectsPage
        ? projectCreditsCurrent
        : messageCreditsCurrent
    const creditsMax = isProjectsPage ? projectCreditsMax : messageCreditsMax
    const creditsPercent =
        creditsMax > 0
            ? Math.min(100, Math.round((creditsCurrent / creditsMax) * 100))
            : 0

    return (
        <>
            <Section show={show} data-leftbar>
                <ScrollWrapper>
                    <TopFixed>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '0 14px',

                                width: '100%',
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                }}
                            >
                                <Logo src={IMAGES.logo} alt="Logo Schief.ai" />
                            </div>
                            <IconButton
                                onClick={() => setShow((prev) => !prev)}
                                aria-label={
                                    show
                                        ? 'Fechar menu lateral'
                                        : 'Abrir menu lateral'
                                }
                                aria-expanded={show}
                                size="small"
                                sx={{
                                    borderRadius: '8px',
                                    '&:hover': {backgroundColor: '#f2f5f6'},
                                }}
                            >
                                <PanelLeft
                                    size={18}
                                    strokeWidth={1.9}
                                    color="#33454e"
                                />
                            </IconButton>
                        </div>

                        {(canAccessChats ||
                            canAccessDocuments ||
                            canAccessAnalyzer ||
                            canAccessPriceResearch ||
                            canAccessCarona ||
                            canAccessRadar ||
                            canAccessLevantamento ||
                            canAccessTrilha) && (
                            <NavGroup>
                                {canAccessChats && (
                                    <NavButton
                                        tooltip="Criar nova consulta"
                                        label="Nova consulta"
                                        icon={
                                            <MessagesSquare
                                                size={18}
                                                strokeWidth={1.9}
                                            />
                                        }
                                        active={
                                            location.pathname === '/' &&
                                            (!selected ||
                                                selected.guid === 'new')
                                        }
                                        onClick={handleChatsClick}
                                    />
                                )}

                                {canAccessDocuments && !isProcon && (
                                    <NavButton
                                        tooltip="Documentos"
                                        label="Documentos"
                                        icon={
                                            <Folder size={18} strokeWidth={1.9} />
                                        }
                                        active={
                                            location.pathname === '/projetos'
                                        }
                                        onClick={handleDocumentosClick}
                                    />
                                )}

                                {canAccessAnalyzer && !isProcon && (
                                    <NavButton
                                        tooltip="Analisar documentos"
                                        label="Análise de Documentos"
                                        icon={
                                            <FileSearch
                                                size={18}
                                                strokeWidth={1.9}
                                            />
                                        }
                                        active={
                                            location.pathname === '/analisador'
                                        }
                                        onClick={() => navigate('/analisador')}
                                    />
                                )}

                                {canAccessPriceResearch && (
                                    <NavButton
                                        tooltip="Pesquisa de Preços"
                                        label="Pesquisa de Preços"
                                        icon={
                                            <Receipt
                                                size={18}
                                                strokeWidth={1.9}
                                                color={COLORS.cinzaEscuro}
                                            />
                                        }
                                        active={
                                            location.pathname ===
                                            '/pesquisa-precos'
                                        }
                                        onClick={() =>
                                            navigate('/pesquisa-precos')
                                        }
                                        isNew
                                    />
                                )}

                                {canAccessLevantamento && (
                                    <NavButton
                                        tooltip="Levantamento de Mercado"
                                        label="Levantamento de Mercado"
                                        icon={
                                            <Compass
                                                size={18}
                                                strokeWidth={1.9}
                                                color={COLORS.cinzaEscuro}
                                            />
                                        }
                                        active={
                                            location.pathname ===
                                            '/levantamento-mercado'
                                        }
                                        onClick={() =>
                                            navigate('/levantamento-mercado')
                                        }
                                        isNew
                                    />
                                )}

                                {canAccessRadar && (
                                    <NavButton
                                        tooltip="Radar de Fornecedor"
                                        label="Radar de Fornecedor"
                                        icon={
                                            <Radar
                                                size={18}
                                                strokeWidth={1.9}
                                                color={COLORS.cinzaEscuro}
                                            />
                                        }
                                        active={
                                            location.pathname ===
                                            '/radar-fornecedor'
                                        }
                                        onClick={() =>
                                            navigate('/radar-fornecedor')
                                        }
                                        isNew
                                    />
                                )}

                                {canAccessCarona && (
                                    <NavButton
                                        tooltip="Ata Inteligente"
                                        label="Ata Inteligente"
                                        icon={
                                            <Handshake
                                                size={18}
                                                strokeWidth={1.9}
                                                color={COLORS.cinzaEscuro}
                                            />
                                        }
                                        active={
                                            location.pathname ===
                                            '/ata-inteligente'
                                        }
                                        onClick={() =>
                                            navigate('/ata-inteligente')
                                        }
                                        isNew
                                    />
                                )}

                                {canAccessTrilha && (
                                    <NavButton
                                        tooltip="Trilha do Guru"
                                        label="Trilha do Guru"
                                        icon={
                                            <GraduationCap
                                                size={18}
                                                strokeWidth={1.9}
                                                color={COLORS.cinzaEscuro}
                                            />
                                        }
                                        active={location.pathname.startsWith(
                                            // prefixo, não igualdade: em
                                            // /trilha-guru/admin o módulo
                                            // continua ativo no menu (mesmo
                                            // critério do isTrilhaPage)
                                            '/trilha-guru'
                                        )}
                                        onClick={() => navigate('/trilha-guru')}
                                        isNew
                                    />
                                )}
                            </NavGroup>
                        )}

                        {isChat && location.pathname === '/' && (
                            <TextField
                                size="small"
                                variant="outlined"
                                placeholder="Procure sua consulta..."
                                value={searchTerm}
                                onChange={(e) =>
                                    setSearchTerm(e.target.value)
                                }
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search
                                                size={16}
                                                strokeWidth={1.9}
                                            />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    mx: 2,
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '10px',
                                        backgroundColor: '#f2f5f6',
                                        '& fieldset': {
                                            borderColor: 'transparent',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: 'transparent',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#3294EF',
                                            borderWidth: '1.5px',
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        fontSize: '13px',
                                    },
                                    '& .MuiInputBase-input::placeholder': {
                                        fontSize: '13px',
                                    },
                                }}
                            />
                        )}
                    </TopFixed>

                    <ScrollArea>
                        {isChat && location.pathname === '/' ? (
                            <History searchTerm={searchTerm} />
                        ) : null}
                    </ScrollArea>
                </ScrollWrapper>

                <BottomFixed ref={listRef}>
                    {openList && <NavList />}
                    <Divider />
                    <SupportButton onClick={() => navigate('/suporte')}>
                        <LifeBuoy size={16} strokeWidth={1.9} />
                        Falar com o suporte
                    </SupportButton>
                    <ButtonsContainer>
                        <CreditsContainer>
                            <CreditsContainerTop>
                                <span>
                                    {isTrilhaPage
                                        ? 'Trilha do Guru'
                                        : isProjectsPage
                                          ? 'Crédito de Documentos'
                                          : 'Crédito de Consultas'}
                                </span>
                                <b
                                    style={{
                                        fontVariantNumeric: 'tabular-nums',
                                        color: '#0c1f29',
                                    }}
                                >
                                    {isTrilhaPage
                                        ? 'Módulo livre'
                                        : `${creditsCurrent} / ${creditsMax}`}
                                </b>
                            </CreditsContainerTop>
                            {!isTrilhaPage && (
                                <CreditsBar>
                                    <CreditsBarFill percent={creditsPercent} />
                                </CreditsBar>
                            )}
                        </CreditsContainer>
                        <ProfileRow>
                            <img
                                src={createInitialsSVG(
                                    userInfo.first_name,
                                    userInfo.last_name
                                )}
                                alt=""
                                style={{
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '9px',
                                    flexShrink: 0,
                                }}
                            />
                            <ProfileInfo>
                                <ProfileName>
                                    {userInfo.first_name} {userInfo.last_name}
                                </ProfileName>
                                <ProfileEmail>{userInfo.email}</ProfileEmail>
                            </ProfileInfo>
                            <Tooltip title="Configurações">
                                <IconButton
                                    onClick={() => setOpenList((prev) => !prev)}
                                    aria-label="Abrir configurações"
                                    size="small"
                                    sx={{
                                        borderRadius: '8px',
                                        flexShrink: 0,
                                        marginLeft: 'auto',
                                        '&:hover': {
                                            backgroundColor: '#f2f5f6',
                                        },
                                    }}
                                >
                                    <Settings
                                        size={18}
                                        strokeWidth={1.9}
                                        color={COLORS.cinzaEscuro}
                                    />
                                </IconButton>
                            </Tooltip>
                        </ProfileRow>
                    </ButtonsContainer>
                </BottomFixed>
            </Section>
            <HideShowBarButton
                position="left"
                offset={show ? LEFTBAR_WIDTH : '0px'}
                show={show}
                setShow={setShow}
            />
        </>
    )
}
